import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { sendMessage } from '../../lib/messages';
import { BUILDINGS } from '../../lib/workerData';

const EMPLOYMENT_OPTIONS = [
  { value: 'full_time', label: 'Full-Time', color: '#2F7A4F' },
  { value: 'part_time', label: 'Part-Time', color: '#2D5F7C' },
  { value: 'on_call', label: 'On-Call', color: '#5B6B72' },
];

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_SHORT = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };
const SHIFT_OPTIONS = [
  { value: '', label: 'Off', color: '#5B6B72' },
  { value: 'day1', label: 'Day 1', color: '#2F7A4F' },
  { value: 'day2', label: 'Day 2', color: '#2D5F7C' },
  { value: 'night', label: 'Night', color: '#6D5AA6' },
];

function defaultForm() {
  return {
    full_name: '',
    email: '',
    phone: '',
    role: 'worker',
    employment_type: 'full_time',
    contract: 'paradise',
    conduct_check: false,
    vulnerable_sector: false,
    whmis: false,
    secure_access: false,
    notes: '',
    shift: '',
    weekly_shifts: {},
    assigned_buildings: [],
  };
}

export default function StaffManager() {
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingWorker, setEditingWorker] = useState(null);
  const [form, setForm] = useState(defaultForm());
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { profile: myProfile } = useAuth();
  const [msgTo, setMsgTo] = useState(null);
  const [msgBody, setMsgBody] = useState('');
  const [msgBusy, setMsgBusy] = useState(false);
  const [msgError, setMsgError] = useState(null);
  // Saving/sending used to close the form with no visible result, so a
  // success was indistinguishable from nothing happening.
  const [successMsg, setSuccessMsg] = useState(null);

  function flashSuccess(text) {
    setSuccessMsg(text);
    setTimeout(() => setSuccessMsg(cur => (cur === text ? null : cur)), 5000);
  }

  const loadWorkers = useCallback(async () => {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .order('full_name');
    setWorkers(data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadWorkers();
  }, [loadWorkers]);

  function openAddForm() {
    setEditingWorker(null);
    setForm(defaultForm());
    setNewPassword('');
    setShowForm(true);
  }

  function openEditForm(worker) {
    setEditingWorker(worker);
    setForm({
      full_name: worker.full_name || '',
      email: worker.email || '',
      phone: worker.phone || '',
      role: worker.role || 'worker',
      employment_type: worker.employment_type || 'full_time',
      contract: worker.contract || 'paradise',
      conduct_check: worker.conduct_check || false,
      vulnerable_sector: worker.vulnerable_sector || false,
      whmis: worker.whmis || false,
      secure_access: worker.secure_access || false,
      notes: worker.notes || '',
      shift: worker.shift || '',
      weekly_shifts: worker.weekly_shifts || {},
      assigned_buildings: worker.assigned_buildings || [],
    });
    setNewPassword('');
    setShowForm(true);
  }

  async function handleSave() {
    setSaving(true);
    try {
      const profileData = {
        full_name: form.full_name,
        phone: form.phone,
        role: form.role,
        employment_type: form.employment_type,
        contract: form.contract,
        conduct_check: form.conduct_check,
        vulnerable_sector: form.vulnerable_sector,
        whmis: form.whmis,
        secure_access: form.secure_access,
        shift: form.shift || null,
        assigned_buildings: form.assigned_buildings,
        notes: form.notes,
        // Store only non-empty day entries; empty object -> null to fall back to `shift`.
        weekly_shifts: Object.keys(form.weekly_shifts || {}).length > 0 ? form.weekly_shifts : null,
      };

      if (editingWorker) {
        const { error } = await supabase.from('profiles').update(profileData).eq('id', editingWorker.id);
        if (error) throw error;
      } else {
        // Create the new account via a service-role edge function instead
        // of supabase.auth.signUp() — signUp() runs in the manager's own
        // browser session and can switch that session over to the new
        // account, silently logging Fabian out mid-flow.
        const { data, error } = await supabase.functions.invoke('create-worker', {
          body: { email: form.email, password: newPassword, profile: profileData },
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
      }
      await loadWorkers();
      setShowForm(false);
      flashSuccess(editingWorker
        ? `Saved changes for ${form.full_name}.`
        : `Account created for ${form.full_name}. Give them their password so they can sign in.`);
    } catch (err) {
      alert('Error: ' + err.message);
    }
    setSaving(false);
  }

  async function handleDelete(worker) {
    if (worker.role === 'manager') {
      alert('Managers cannot be removed from here.');
      return;
    }
    if (!window.confirm(`Remove ${worker.full_name}? This deletes their login and cannot be undone. Their past checklist/report history is kept.`)) return;
    try {
      const { data, error } = await supabase.functions.invoke('delete-worker', {
        body: { user_id: worker.id },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      await loadWorkers();
      flashSuccess(`${worker.full_name} was removed.`);
    } catch (err) {
      alert(`Could not remove ${worker.full_name}: ${err.message}`);
    }
  }

  async function handleSendMessage() {
    if (!msgTo || !msgBody.trim() || !myProfile?.id) return;
    setMsgBusy(true);
    setMsgError(null);
    try {
      await sendMessage({
        senderId: myProfile.id,
        senderRole: myProfile.role,
        recipientId: msgTo.id,
        body: msgBody,
      });
      const sentTo = msgTo.full_name;
      setMsgBody('');
      setMsgTo(null);
      flashSuccess(`Message sent to ${sentTo}.`);
    } catch (err) {
      setMsgError(err.message);
    }
    setMsgBusy(false);
  }

  const paradiseWorkers = workers.filter(w => w.contract === 'paradise' && w.role === 'worker');
  const paradiseSupervisors = workers.filter(w => w.contract === 'paradise' && w.role === 'supervisor');
  const managers = workers.filter(w => w.role === 'manager');
  // Catches anyone (worker or supervisor) not on the 'paradise' contract tag —
  // previously only caught role === 'worker', so a non-paradise supervisor had
  // no bucket at all and never rendered anywhere on this screen.
  const otherWorkers = workers.filter(w => w.contract !== 'paradise' && w.role !== 'manager');
  // Header tally counts everyone by role regardless of contract — it was
  // previously built from the paradise-only arrays above, so it undercounted
  // (or zeroed) the moment any profile had a non-'paradise' contract value.
  const totalWorkers = workers.filter(w => w.role === 'worker').length;
  const totalSupervisors = workers.filter(w => w.role === 'supervisor').length;

  function clearanceScore(w) {
    return [w.conduct_check, w.vulnerable_sector, w.whmis, w.secure_access].filter(Boolean).length;
  }
  function clearanceColor(score) {
    if (score === 4) return '#2F7A4F';
    if (score >= 2) return '#B5730A';
    return '#B33A2E';
  }

  if (loading) return <div style={s.loading}>Loading staff...</div>;

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <div style={s.title}>Staff Management</div>
          <div style={s.subtitle}>
            {totalWorkers} worker{totalWorkers !== 1 ? 's' : ''} · {totalSupervisors} supervisor{totalSupervisors !== 1 ? 's' : ''} · {managers.length} manager{managers.length !== 1 ? 's' : ''}
          </div>
        </div>
        <button style={s.addBtn} onClick={openAddForm}>+ Add Worker</button>
      </div>

      {successMsg && (
        <div style={{ margin: '0 12px 12px', background: 'rgba(47,122,79,0.10)', border: '1px solid #2F7A4F', borderRadius: 8, padding: '10px 14px', color: '#2F7A4F', fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <span>✓ {successMsg}</span>
          <button style={{ background: 'none', border: 'none', color: '#2F7A4F', cursor: 'pointer', fontSize: 12, textDecoration: 'underline' }} onClick={() => setSuccessMsg(null)}>Dismiss</button>
        </div>
      )}

      {workers.length === 0 && (
        <div style={s.emptyState}>No staff on file yet. Use "+ Add Worker" to add the first one.</div>
      )}

      {/* Managers */}
      {managers.length > 0 && (
        <div style={s.section}>
          <div style={s.sectionLabel}>Managers</div>
          {managers.map(w => (
            <WorkerCard key={w.id} worker={w} expanded={expandedId === w.id}
              onExpand={() => setExpandedId(expandedId === w.id ? null : w.id)}
              onEdit={() => openEditForm(w)} onDelete={() => handleDelete(w)} onMessage={w => setMsgTo(w)}
              clearanceScore={clearanceScore(w)} clearanceColor={clearanceColor(clearanceScore(w))} />
          ))}
        </div>
      )}

      {/* Supervisors */}
      {paradiseSupervisors.length > 0 && (
        <div style={s.section}>
          <div style={s.sectionLabel}>Supervisors</div>
          {paradiseSupervisors.map(w => (
            <WorkerCard key={w.id} worker={w} expanded={expandedId === w.id}
              onExpand={() => setExpandedId(expandedId === w.id ? null : w.id)}
              onEdit={() => openEditForm(w)} onDelete={() => handleDelete(w)} onMessage={w => setMsgTo(w)}
              clearanceScore={clearanceScore(w)} clearanceColor={clearanceColor(clearanceScore(w))} />
          ))}
        </div>
      )}

      {/* Paradise Workers */}
      {paradiseWorkers.length > 0 && (
        <div style={s.section}>
          <div style={s.sectionLabel}>Paradise Workers</div>
          {paradiseWorkers.map(w => (
            <WorkerCard key={w.id} worker={w} expanded={expandedId === w.id}
              onExpand={() => setExpandedId(expandedId === w.id ? null : w.id)}
              onEdit={() => openEditForm(w)} onDelete={() => handleDelete(w)} onMessage={w => setMsgTo(w)}
              clearanceScore={clearanceScore(w)} clearanceColor={clearanceColor(clearanceScore(w))} />
          ))}
        </div>
      )}

      {/* Other Contract Workers */}
      {otherWorkers.length > 0 && (
        <div style={s.section}>
          <div style={s.sectionLabel}>Other Contracts</div>
          {otherWorkers.map(w => (
            <WorkerCard key={w.id} worker={w} expanded={expandedId === w.id}
              onExpand={() => setExpandedId(expandedId === w.id ? null : w.id)}
              onEdit={() => openEditForm(w)} onDelete={() => handleDelete(w)} onMessage={w => setMsgTo(w)}
              clearanceScore={clearanceScore(w)} clearanceColor={clearanceColor(clearanceScore(w))} />
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showForm && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <div style={s.modalHeader}>
              <div style={s.modalTitle}>{editingWorker ? `Edit: ${editingWorker.full_name}` : 'Add New Worker'}</div>
              <button style={s.closeBtn} onClick={() => setShowForm(false)}>✕</button>
            </div>
            <div style={s.modalBody}>
              {/* Name */}
              <div style={s.formSection}>PERSONAL INFO</div>
              <input style={s.input} placeholder="Full Name *" value={form.full_name}
                onChange={e => setForm(p => ({ ...p, full_name: e.target.value }))} />
              {/* Email is only editable at creation — the save path updates
                  the profile row, not the auth login, so letting it be typed
                  over during an edit looked like it worked and silently
                  didn't. */}
              {editingWorker ? (
                <>
                  <input style={{ ...s.input, color: '#5B6B72' }} value={form.email} readOnly disabled />
                  <div style={{ fontSize: 12, color: '#5B6B72', marginTop: -6, marginBottom: 10 }}>
                    Sign-in email can't be changed here. Remove the worker and re-add them to use a different email.
                  </div>
                </>
              ) : (
                <input style={s.input} placeholder="Email *" value={form.email}
                  onChange={e => setForm(p => ({ ...p, email: e.target.value }))} />
              )}
              {!editingWorker && (
                <div style={{ position: 'relative' }}>
                  <input style={{ ...s.input, paddingRight: 60 }} placeholder="Temporary Password *" type={showPassword ? 'text' : 'password'}
                    value={newPassword} onChange={e => setNewPassword(e.target.value)} />
                  <button type="button" style={s.showPwBtn} onClick={() => setShowPassword(p => !p)}>
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              )}
              <input style={s.input} placeholder="Phone" value={form.phone}
                onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} />

              {/* Role */}
              <div style={s.formSection}>ROLE</div>
              <div style={s.btnRow}>
                {['worker', 'supervisor', 'manager'].map(r => (
                  <button key={r} style={form.role === r ? { ...s.roleBtn, background: '#f97316', color: '#000' } : s.roleBtn}
                    onClick={() => setForm(p => ({ ...p, role: r }))}>
                    {r.charAt(0).toUpperCase() + r.slice(1)}
                  </button>
                ))}
              </div>

              {/* Employment Type */}
              <div style={s.formSection}>EMPLOYMENT TYPE</div>
              <div style={s.btnRow}>
                {EMPLOYMENT_OPTIONS.map(opt => (
                  <button key={opt.value}
                    style={form.employment_type === opt.value
                      ? { ...s.roleBtn, background: opt.color, color: '#000' }
                      : s.roleBtn}
                    onClick={() => setForm(p => ({ ...p, employment_type: opt.value }))}>
                    {opt.label}
                  </button>
                ))}
              </div>

              {/* Contract Tag */}
              <div style={s.formSection}>CONTRACT</div>
              <div style={s.btnRow}>
                <button style={form.contract === 'paradise' ? { ...s.roleBtn, background: '#2F7A4F', color: '#FFFFFF' } : s.roleBtn}
                  onClick={() => setForm(p => ({ ...p, contract: 'paradise' }))}>Paradise</button>
                <button style={form.contract === 'other' ? { ...s.roleBtn, background: '#5B6B72', color: '#fff' } : s.roleBtn}
                  onClick={() => setForm(p => ({ ...p, contract: 'other' }))}>Other</button>
              </div>

              {/* Default Shift — the fallback used for any day without a
                  per-day override below (profiles.shift). */}
              <div style={s.formSection}>DEFAULT SHIFT</div>
              <div style={{ fontSize: 12, color: '#5B6B72', marginBottom: 8, lineHeight: 1.5 }}>
                Day 1 = 8am–3pm · Day 2 = 3pm–10pm · Night = 10pm–6am. This is the shift used on any day
                not set individually in the weekly schedule below.
              </div>
              <div style={s.btnRow}>
                {SHIFT_OPTIONS.map(opt => (
                  <button key={opt.value || 'off'}
                    style={form.shift === opt.value
                      ? { ...s.roleBtn, background: opt.color, color: '#FFFFFF' }
                      : s.roleBtn}
                    onClick={() => setForm(p => ({ ...p, shift: opt.value }))}>
                    {opt.label}
                  </button>
                ))}
              </div>

              {/* Assigned Buildings — drives supervisor building-scoped RLS
                  policies and which buildings a worker sees in the app. */}
              <div style={s.formSection}>ASSIGNED BUILDINGS</div>
              <div style={{ fontSize: 12, color: '#5B6B72', marginBottom: 8, lineHeight: 1.5 }}>
                Leave empty to allow all buildings. Otherwise this person only sees the buildings checked below when they sign in.
              </div>
              <div style={s.btnRow}>
                {BUILDINGS.filter(b => b.status !== 'special' && !b.mergedInto).map(b => {
                  const active = form.assigned_buildings.includes(b.id) || (b.id === 'E' && form.assigned_buildings.includes('F'));
                  return (
                    <button key={b.id}
                      style={active ? { ...s.roleBtn, background: '#1e3a5f', border: '1px solid #3b82f6', color: '#93c5fd' } : s.roleBtn}
                      onClick={() => setForm(p => ({
                        ...p,
                        assigned_buildings: active
                          ? p.assigned_buildings.filter(id => id !== b.id && !(b.id === 'E' && id === 'F'))
                          : [...p.assigned_buildings, b.id],
                      }))}>
                      {active ? '✓ ' : ''}{b.shortName || b.name} ({b.id})
                    </button>
                  );
                })}
              </div>

              {/* Weekly Shift Schedule — per-day override for split/varying schedules.
                  Leave every day "Off" to keep using the worker's single default shift. */}
              <div style={s.formSection}>WEEKLY SHIFT SCHEDULE</div>
              <div style={{ fontSize: 12, color: '#5B6B72', marginBottom: 8, lineHeight: 1.5 }}>
                Set a specific shift per day for anyone whose schedule varies (days some days, nights others).
                Leave every day "Off" to use the default shift above for the whole week.
              </div>
              {DAYS.map(day => (
                <div key={day} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <div style={{ width: 36, fontSize: 12, fontWeight: 700, color: '#5B6B72' }}>{DAY_SHORT[day]}</div>
                  <div style={{ display: 'flex', gap: 4, flex: 1 }}>
                    {SHIFT_OPTIONS.map(opt => {
                      const active = (form.weekly_shifts[day] || '') === opt.value;
                      return (
                        <button key={opt.value || 'off'}
                          style={{
                            flex: 1, padding: '6px 0', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                            border: active ? `1px solid ${opt.color}` : '1px solid #E4E0D8',
                            background: active ? opt.color + '22' : '#FFFFFF',
                            color: active ? opt.color : '#5B6B72',
                          }}
                          onClick={() => setForm(p => {
                            const next = { ...p.weekly_shifts };
                            if (opt.value) next[day] = opt.value; else delete next[day];
                            return { ...p, weekly_shifts: next };
                          })}>
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Password Reset — only when editing */}
              {editingWorker && (
                <>
                  <div style={s.formSection}>RESET PASSWORD</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <div style={{ position: 'relative', flex: 1 }}>
                      <input style={{ ...s.input, width: '100%', boxSizing: 'border-box', paddingRight: 60 }} placeholder="New password (min 6 chars)" type={showPassword ? 'text' : 'password'}
                        value={newPassword} onChange={e => setNewPassword(e.target.value)} />
                      <button type="button" style={s.showPwBtn} onClick={() => setShowPassword(p => !p)}>
                        {showPassword ? 'Hide' : 'Show'}
                      </button>
                    </div>
                    <button
                      style={{
                        background: newPassword.length >= 6 ? '#2D5F7C' : '#E4E0D8',
                        border: 'none', color: newPassword.length >= 6 ? '#fff' : '#5B6B72',
                        borderRadius: 8, padding: '0 16px', fontWeight: 700, fontSize: 13, cursor: newPassword.length >= 6 ? 'pointer' : 'not-allowed',
                        whiteSpace: 'nowrap',
                      }}
                      disabled={newPassword.length < 6}
                      onClick={async () => {
                        if (newPassword.length < 6) return;
                        try {
                          const { error } = await supabase.functions.invoke('reset-password', {
                            body: { user_id: editingWorker.id, new_password: newPassword },
                          });
                          if (error) throw error;
                          alert('Password updated successfully!');
                          setNewPassword('');
                        } catch (err) {
                          // No plaintext-SQL fallback here anymore — handing
                          // out a raw `UPDATE auth.users SET encrypted_password
                          // = crypt(...)` string (with the plaintext password
                          // inline) on any network hiccup was a real
                          // credential-exposure risk. Just report the failure.
                          console.error('Password reset failed:', err);
                          alert('Could not reset the password. Check your connection and try again in a minute.');
                        }
                      }}>
                      Reset
                    </button>
                  </div>
                </>
              )}

              {/* Clearances */}
              <div style={s.formSection}>CLEARANCES</div>
              {[
                { key: 'conduct_check', label: 'Certificate of Conduct' },
                { key: 'vulnerable_sector', label: 'Vulnerable Sector Check' },
                { key: 'whmis', label: 'WHMIS Training' },
                { key: 'secure_access', label: 'Secure Access / Keys' },
              ].map(item => (
                <button key={item.key}
                  style={form[item.key]
                    ? { ...s.clearanceBtn, background: 'rgba(47,122,79,0.10)', border: '1px solid #2F7A4F', color: '#2F7A4F' }
                    : s.clearanceBtn}
                  onClick={() => setForm(p => ({ ...p, [item.key]: !p[item.key] }))}>
                  {form[item.key] ? '✓ ' : '○ '}{item.label}
                </button>
              ))}

              <div style={s.formSection}>NOTES</div>
              <textarea style={s.textarea} rows={3} placeholder="Any relevant notes..."
                value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
            </div>
            <div style={s.modalFooter}>
              <button style={s.cancelBtn} onClick={() => setShowForm(false)}>Cancel</button>
              <button
                style={(!form.full_name || saving || (!editingWorker && (!form.email || !newPassword))) ? { ...s.saveBtn, opacity: 0.5 } : s.saveBtn}
                disabled={!form.full_name || saving || (!editingWorker && (!form.email || !newPassword))}
                onClick={handleSave}>
                {saving ? 'Saving...' : editingWorker ? 'Save Changes' : 'Create Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Message Modal */}
      {msgTo && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <div style={s.modalHeader}>
              <div style={s.modalTitle}>Message {msgTo.full_name}</div>
              <button style={s.closeBtn} onClick={() => setMsgTo(null)}>✕</button>
            </div>
            <div style={s.modalBody}>
              {msgError && <div style={{ color: '#B33A2E', fontSize: 13, marginBottom: 8 }}>{msgError}</div>}
              <textarea style={s.textarea} rows={4} placeholder={`Type a message to ${msgTo.full_name}...`}
                value={msgBody} onChange={e => setMsgBody(e.target.value)} />
              <button
                style={(!msgBody.trim() || msgBusy) ? { ...s.saveBtn, opacity: 0.5 } : s.saveBtn}
                disabled={!msgBody.trim() || msgBusy}
                onClick={handleSendMessage}>
                {msgBusy ? 'Sending…' : 'Send Message'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function WorkerCard({ worker, expanded, onExpand, onEdit, onDelete, onMessage, clearanceScore, clearanceColor }) {
  const empType = EMPLOYMENT_OPTIONS.find(r => r.value === worker.employment_type);

  return (
    <div style={s.card}>
      <div style={s.cardTop} onClick={onExpand}>
        <div style={s.cardLeft}>
          <div style={s.avatar}>{(worker.full_name || '?')[0].toUpperCase()}</div>
          <div>
            <div style={s.workerName}>{worker.full_name}</div>
            <div style={s.workerMeta}>
              {worker.role === 'manager'
                ? <span style={{ color: '#1F3A47', fontWeight: 700, fontSize: 11 }}>MANAGER</span>
                : worker.role === 'supervisor'
                  ? <span style={{ color: '#6D5AA6', fontWeight: 700, fontSize: 11 }}>SUPERVISOR</span>
                  : <span style={{ color: empType?.color || '#5B6B72', fontSize: 11, fontWeight: 600 }}>{empType?.label || 'Worker'}</span>}
            </div>
          </div>
        </div>
        <div style={s.cardRight}>
          <div style={{ ...s.clearancePip, background: clearanceColor }}>{clearanceScore}/4</div>
          <div style={s.chevron}>{expanded ? '▲' : '▼'}</div>
        </div>
      </div>
      {expanded && (
        <div style={s.cardBody}>
          {worker.email && <div style={s.detail}>✉ {worker.email}</div>}
          {worker.phone && <div style={s.detail}>📞 {worker.phone}</div>}
          <div style={s.detail}>
            {worker.conduct_check ? '✅' : '❌'} Conduct
            {' '}{worker.vulnerable_sector ? '✅' : '❌'} Vulnerable
            {' '}{worker.whmis ? '✅' : '❌'} WHMIS
            {' '}{worker.secure_access ? '✅' : '❌'} Access
          </div>
          {worker.notes && <div style={{ ...s.detail, fontStyle: 'italic', color: '#5B6B72' }}>📝 {worker.notes}</div>}
          <div style={s.cardActions}>
            <button style={s.editBtn} onClick={onEdit}>Edit</button>
            <button style={s.msgBtn2} onClick={() => onMessage(worker)}>Message</button>
            {worker.role !== 'manager' && (
              <button style={s.deleteBtn} onClick={onDelete}>Remove</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  page: { padding: 16, background: '#F5F3EF', minHeight: 'calc(100vh - 130px)' },
  loading: { padding: 40, textAlign: 'center', color: '#5B6B72' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
  title: { fontSize: 22, fontWeight: 800, color: '#1A2B33' },
  subtitle: { fontSize: 13, color: '#5B6B72', marginTop: 2 },
  addBtn: { background: '#f97316', border: 'none', color: '#FFFFFF', padding: '8px 16px', borderRadius: 8, fontWeight: 800, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' },

  section: { marginBottom: 20 },
  sectionLabel: { fontSize: 11, fontWeight: 700, color: '#5B6B72', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 },

  card: { background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 12, marginBottom: 8, overflow: 'hidden' },
  cardTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', cursor: 'pointer' },
  cardLeft: { display: 'flex', alignItems: 'center', gap: 12 },
  cardRight: { display: 'flex', alignItems: 'center', gap: 10 },
  avatar: { width: 36, height: 36, borderRadius: 10, background: '#F5F3EF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: '#1A2B33', fontSize: 15 },
  workerName: { fontSize: 14, fontWeight: 700, color: '#1A2B33' },
  workerMeta: { fontSize: 11, marginTop: 2, display: 'flex', alignItems: 'center' },
  clearancePip: { fontSize: 11, fontWeight: 800, color: '#fff', padding: '2px 8px', borderRadius: 10 },
  chevron: { color: '#5B6B72', fontSize: 10 },
  cardBody: { padding: '0 14px 14px', borderTop: '1px solid #E4E0D8' },
  detail: { fontSize: 13, color: '#1A2B33', padding: '6px 0', borderBottom: '1px solid #F5F3EF' },
  cardActions: { display: 'flex', gap: 8, marginTop: 10 },
  editBtn: { flex: 1, padding: '8px 0', background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 8, color: '#1A2B33', fontSize: 13, fontWeight: 600, cursor: 'pointer', textAlign: 'center' },
  msgBtn2: { flex: 1, padding: '8px 0', background: 'rgba(31,58,71,0.08)', border: '1px solid #1F3A47', borderRadius: 8, color: '#1F3A47', fontSize: 13, fontWeight: 600, cursor: 'pointer', textAlign: 'center' },
  deleteBtn: { padding: '8px 16px', background: 'transparent', border: '1px solid #B33A2E', borderRadius: 8, color: '#B33A2E', fontSize: 13, fontWeight: 600, cursor: 'pointer' },

  // Modal
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: 40, overflowY: 'auto' },
  modal: { background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 16, width: '100%', maxWidth: 500, margin: '0 16px 40px' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E4E0D8' },
  modalTitle: { fontSize: 18, fontWeight: 800, color: '#1A2B33' },
  closeBtn: { background: 'none', border: 'none', color: '#5B6B72', fontSize: 20, cursor: 'pointer' },
  modalBody: { padding: '16px 20px', maxHeight: '60vh', overflowY: 'auto' },
  modalFooter: { display: 'flex', gap: 10, padding: '16px 20px', borderTop: '1px solid #E4E0D8' },

  formSection: { fontSize: 10, fontWeight: 700, color: '#5B6B72', textTransform: 'uppercase', letterSpacing: 1, marginTop: 16, marginBottom: 8 },
  input: { width: '100%', padding: '10px 12px', background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 8, color: '#1A2B33', fontSize: 14, marginBottom: 8, outline: 'none', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '10px 12px', background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 8, color: '#1A2B33', fontSize: 14, outline: 'none', resize: 'vertical', boxSizing: 'border-box' },
  btnRow: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 4 },
  roleBtn: { padding: '8px 14px', background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 8, color: '#5B6B72', fontSize: 13, fontWeight: 600, cursor: 'pointer' },

  emptyState: { background: '#FFFFFF', border: '1px dashed #E4E0D8', borderRadius: 10, padding: 24, textAlign: 'center', color: '#5B6B72', fontSize: 14, margin: '0 12px 12px' },
  showPwBtn: {
    position: 'absolute', top: '50%', right: 6, transform: 'translateY(-50%)',
    background: '#E4E0D8', border: 'none', color: '#1A2B33', fontSize: 11, fontWeight: 700,
    borderRadius: 6, padding: '5px 10px', cursor: 'pointer',
  },
  clusterGroup: { marginBottom: 12 },
  clusterLabel: { fontSize: 11, color: '#5B6B72', fontWeight: 600, marginBottom: 6 },
  buildingBtn: { display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 8, color: '#5B6B72', fontSize: 13, fontWeight: 500, cursor: 'pointer', marginBottom: 4 },

  // Shift assignment
  shiftNotice: { fontSize: 12, color: '#1F3A47', background: 'rgba(31,58,71,0.08)', border: '1px solid #1F3A47', borderRadius: 8, padding: '8px 12px', marginBottom: 10 },
  shiftGroup: { marginBottom: 12 },
  shiftBuildingLabel: { fontSize: 12, fontWeight: 700, color: '#5B6B72', marginBottom: 6 },
  shiftBtn: { flex: 1, padding: '10px 14px', background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 8, cursor: 'pointer', textAlign: 'center', minWidth: 120 },
  shiftBtnLabel: { fontSize: 13, fontWeight: 700, color: '#1A2B33' },
  shiftBtnTime: { fontSize: 11, color: '#5B6B72', marginTop: 2 },

  clearanceBtn: { display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 8, color: '#5B6B72', fontSize: 13, fontWeight: 500, cursor: 'pointer', marginBottom: 4 },

  cancelBtn: { flex: 1, background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 8, padding: '12px 0', color: '#5B6B72', fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  saveBtn: { flex: 2, background: '#f97316', border: 'none', color: '#FFFFFF', borderRadius: 8, padding: '12px 0', fontWeight: 800, fontSize: 14, cursor: 'pointer' },
};